import React from 'react';
import { Building2, Award, TrendingUp, Rocket, Target } from 'lucide-react';

const Roadmap = () => {
  const milestones = [
    {
      year: '2003',
      title: 'Bhagwati Enterprise Founded',
      description: 'Mr. Subhashbhai Bhuderbhai Detroja began his journey by establishing Bhagwati Enterprise as a partnership firm, laying the groundwork for a long-standing construction legacy.',
      icon: Building2
    },
    {
      year: '2010',
      title: 'Transformation to MHS Infratech Pvt. Ltd.',
      description: 'The organization evolved into a private limited company, expanding into larger and more complex infrastructure projects.',
      icon: TrendingUp
    },
    {
      year: '2017',
      title: 'AA Class Certification – Govt. of Gujarat',
      description: 'Achieved AA Class certification, reinforcing credibility and expertise in government infrastructure projects.',
      icon: Award
    },
    {
      year: '2018',
      title: 'Chevrox Constructions Pvt. Ltd.',
      description: 'The company was rebranded and scaled significantly, entering a new phase of growth and expansion.',
      icon: Rocket
    },
    {
      year: '2019',
      title: 'Landmark Project Execution',
      description: 'Successfully completed one of the largest road sub-contract projects with a contract value of ₹258 Crore, showcasing strong execution capabilities.',
      icon: Target,
      highlight: true
    },
    {
      year: '2023',
      title: 'Peak Growth & Pan-India Presence',
      description: 'Operations expanded across 7+ states in India. At the time of departure, Chevrox Constructions Pvt. Ltd. had achieved an impressive annual revenue exceeding ₹300 Crore, reflecting the scale built under his leadership.',
      icon: TrendingUp,
      highlight: true
    },
    {
      year: 'Present',
      title: 'Florentina Infra LLP – A New Vision',
      description: 'Mr. Subhashbhai Detroja has now embarked on a new journey with Florentina Infra LLP, focusing on private construction projects. With a strategic shift from government contracts to private developments, the company aims to deliver high-quality, modern infrastructure with agility and innovation.',
      icon: Rocket,
      highlight: true,
      featured: true
    }
  ];

  return (
    <section id="roadmap" className="py-20 bg-gradient-to-b from-white via-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-primary font-semibold text-sm tracking-wider uppercase">Our Journey</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            An Arduous Yet Rewarding Road
          </h2>
          <p className="text-gray-600 text-lg max-w-4xl mx-auto leading-relaxed">
            A journey driven by vision, perseverance, and leadership. From humble beginnings to building large-scale infrastructure ventures, this path reflects decades of expertise and transformation. With a strong foundation in government projects and a strategic evolution toward private developments, the legacy continues to grow.
          </p>
        </div>

        {/* Timeline Grid - No connecting lines */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {milestones.map((milestone, index) => {
              const Icon = milestone.icon;
              
              return (
                <div 
                  key={`milestone-${milestone.year}-${index}`}
                  className={`group relative ${
                    milestone.featured ? 'md:col-span-2 lg:col-span-3' : ''
                  }`}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className={`h-full ${
                    milestone.featured 
                      ? 'bg-gradient-to-br from-primary to-[#214979] text-white shadow-2xl transform hover:scale-[1.02]' 
                      : milestone.highlight
                      ? 'bg-gradient-to-br from-primary/5 to-[#c0932a]/5 border-2 border-primary/20 shadow-lg hover:shadow-xl'
                      : 'bg-white shadow-md hover:shadow-lg border border-gray-100'
                  } rounded-xl p-8 transition-all duration-300`}>
                    
                    {/* Year Badge & Icon */}
                    <div className="flex items-center justify-between mb-6">
                      <div className={`inline-block px-5 py-2 rounded-full text-sm font-bold ${
                        milestone.featured 
                          ? 'bg-white/20 text-white backdrop-blur-sm' 
                          : milestone.highlight
                          ? 'bg-primary text-white'
                          : 'bg-primary/10 text-primary'
                      }`}>
                        {milestone.year}
                      </div>
                      
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        milestone.featured 
                          ? 'bg-white/20 backdrop-blur-sm' 
                          : milestone.highlight
                          ? 'bg-primary/10'
                          : 'bg-gray-100'
                      }`}>
                        <Icon 
                          className={milestone.featured || milestone.highlight ? 'text-white' : 'text-primary'} 
                          size={28} 
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className={`text-2xl font-bold mb-4 ${
                      milestone.featured || milestone.highlight ? 'text-current' : 'text-gray-900'
                    }`}>
                      {milestone.title}
                    </h3>

                    {/* Description */}
                    <p className={`leading-relaxed ${
                      milestone.featured 
                        ? 'text-white/95 text-lg' 
                        : milestone.highlight
                        ? 'text-gray-700 text-base'
                        : 'text-gray-600 text-base'
                    }`}>
                      {milestone.description}
                    </p>

                    {milestone.featured && (
                      <div className="mt-8 pt-6 border-t border-white/20">
                        <div className="flex items-center gap-2 text-white/90 font-semibold">
                          <Rocket size={20} />
                          <span>The Future Begins Here</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Quote */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-primary/5 via-[#c0932a]/5 to-primary/5 rounded-2xl p-8 border border-primary/10">
            <p className="text-gray-700 text-lg italic leading-relaxed">
              "Building the future with the wisdom of the past and the vision of tomorrow"
            </p>
            <p className="text-primary font-bold mt-4">– Florentina Infra LLP</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
